import React from 'react';
import { Controller } from 'react-hook-form';
import { Accordion, Drawer, SelectInput, TextInput, Button, Checkbox } from '@sabre/spark-react-core';

interface DynamicFieldRendererProps {
    property: string;
    propertySchema: any;
    parentPath?: string;
    control: any;
    watch: any;
    setValue: any;
    schemaObject: any;
    allExpanded?: boolean;
}

const resolveRef = (schema: any, ref: string): any => {
    if (!ref.startsWith('#/')) return null;

    const path = ref.substring(2).split('/');
    let resolved = schema;

    for (const key of path) {
        resolved = resolved?.[key];
        if (!resolved) return null;
    }

    return resolved;
};

const getTitle = (schema: any, property: string) =>
    schema.title || property.charAt(0).toUpperCase() + property.slice(1);

const CollapsibleSection = ({ property, fieldName, allExpanded, title, children }: {
    property: string;
    fieldName: string;
    allExpanded: boolean;
    title: string;
    children: React.ReactNode;
}) => (
    <Accordion key={property} className="qa-configuration-collapsible-section" style={{ marginBottom: '1rem' }}>
        <Drawer
            headingLevel="h3"
            title={title}
            expanded={allExpanded}
            className={"qa-drawer-header"}
            id={`drawer-${fieldName}`}
        >
            {children}
        </Drawer>
    </Accordion>
);

export const renderDynamicField = ({
                                       property,
                                       propertySchema,
                                       parentPath = 'schemaGUI',
                                       control,
                                       watch,
                                       setValue,
                                       schemaObject,
                                       allExpanded = true
                                   }: DynamicFieldRendererProps) => {
    const fieldName = `${parentPath}.${property}` as const;

    let resolvedSchema = propertySchema;
    if (propertySchema.$ref) {
        const refSchema = resolveRef(schemaObject, propertySchema.$ref);
        if (refSchema) {
            resolvedSchema = { ...propertySchema, ...refSchema };
            delete resolvedSchema.$ref;
        }
    }

    // Array with enum items
    if (resolvedSchema.type === 'array' && resolvedSchema.items?.enum) {
        const fieldValue = watch(fieldName as any);
        const currentValues = Array.isArray(fieldValue) ? fieldValue : [];
        const noValSelect = { label: '-- Select --', value: '' };

        return (
            <CollapsibleSection property={property} fieldName={fieldName} allExpanded={allExpanded} title={getTitle(resolvedSchema, property)}>
                {currentValues.map((value: string, index: number) => (
                    <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                        <SelectInput
                            value={value}
                            onChange={(e) => {
                                const newValues = [...currentValues];
                                newValues[index] = e.target.value;
                                setValue(fieldName as any, newValues);
                            }}
                            className="spark-select"
                            options={[noValSelect, ...resolvedSchema.items.enum.map((enumVal: string) => ({ label: enumVal, value: enumVal }))]}
                        />
                        <Button
                            type="button"
                            size={"sm"}
                            onClick={() => {
                                const newValues = currentValues.filter((_, i) => i !== index);
                                setValue(fieldName as any, newValues);
                            }}
                        >
                            Remove
                        </Button>
                    </div>
                ))}
                <Button
                    type="button"
                    size={"sm"}
                    className={"qa-add-item-button"}
                    onClick={() => setValue(fieldName as any, [...currentValues, ''])}
                >
                    Add Item
                </Button>
                {resolvedSchema.minItems && currentValues.length < resolvedSchema.minItems && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                        Minimum {resolvedSchema.minItems} item(s) required
                    </div>
                )}
            </CollapsibleSection>
        );
    }

    // Array with primitive items
    if (resolvedSchema.type === 'array' && resolvedSchema.items?.type && !resolvedSchema.items.enum && resolvedSchema.items.type !== 'object') {
        const fieldValue = watch(fieldName as any);
        const currentValues = Array.isArray(fieldValue) ? fieldValue : [];
        const itemType = resolvedSchema.items.type;

        return (
            <CollapsibleSection property={property} fieldName={fieldName} allExpanded={allExpanded} title={getTitle(resolvedSchema, property)}>
                {currentValues.map((value: any, index: number) => (
                    <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                        <input
                            type={itemType === 'number' || itemType === 'integer' ? 'number' : 'text'}
                            value={value || ''}
                            onChange={(e) => {
                                const newValues = [...currentValues];
                                newValues[index] = itemType === 'number' || itemType === 'integer' ? Number(e.target.value) : e.target.value;
                                setValue(fieldName as any, newValues);
                            }}
                            className="spark-input"
                            style={{ flex: 1 }}
                        />
                        <Button
                            type="button"
                            size={"sm"}
                            onClick={() => {
                                const newValues = currentValues.filter((_, i) => i !== index);
                                setValue(fieldName as any, newValues);
                            }}
                        >
                            Remove
                        </Button>
                    </div>
                ))}
                <Button
                    type="button"
                    size={"sm"}
                    className={"qa-add-item-button"}
                    onClick={() => setValue(fieldName as any, [...currentValues, ''])}
                >
                    Add Item
                </Button>
                {resolvedSchema.minItems && currentValues.length < resolvedSchema.minItems && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                        Minimum {resolvedSchema.minItems} item(s) required
                    </div>
                )}
            </CollapsibleSection>
        );
    }

    // Array with object items
    if (propertySchema.type === 'array' && propertySchema.items?.type === 'object') {
        const fieldValue = watch(fieldName as any);
        const currentValues = Array.isArray(fieldValue) ? fieldValue : [];

        return (
            <CollapsibleSection property={property} fieldName={fieldName} allExpanded={allExpanded} title={getTitle(propertySchema, property)}>
                {currentValues.map((item: any, index: number) => (
                    <fieldset key={index} style={{ border: '1px dashed #999', padding: '12px', marginBottom: '12px', position: 'relative' }}>
                        <legend>Item {index + 1}</legend>
                        {Object.entries(propertySchema.items.properties || {}).map(([key, prop]: [string, any]) =>
                            renderDynamicField({
                                property: key,
                                propertySchema: prop,
                                parentPath: `${fieldName}.${index}`,
                                control,
                                watch,
                                setValue,
                                schemaObject,
                                allExpanded
                            })
                        )}
                        <Button
                            type="button"
                            size={"sm"}
                            className={"qa-remove-item-button"}
                            onClick={() => {
                                const newValues = currentValues.filter((_, i) => i !== index);
                                setValue(fieldName as any, newValues);
                            }}
                        >
                            Remove Item
                        </Button>
                    </fieldset>
                ))}
                <Button
                    type="button"
                    size={"sm"}
                    className={"qa-add-item-button"}
                    onClick={() => setValue(fieldName as any, [...currentValues, {}])}
                >
                    Add Item
                </Button>
                {propertySchema.minItems && currentValues.length < propertySchema.minItems && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                        Minimum {propertySchema.minItems} item(s) required
                    </div>
                )}
            </CollapsibleSection>
        );
    }

    // Nested objects
    if (propertySchema.type === 'object' && propertySchema.properties) {
        return (
            <CollapsibleSection property={property} fieldName={fieldName} allExpanded={allExpanded} title={getTitle(propertySchema, property)}>
                {Object.entries(propertySchema.properties).map(([nestedKey, nestedProp]: [string, any]) =>
                    renderDynamicField({
                        property: nestedKey,
                        propertySchema: nestedProp,
                        parentPath: fieldName,
                        control,
                        watch,
                        setValue,
                        schemaObject,
                        allExpanded
                    })
                )}
            </CollapsibleSection>
        );
    }

    // Enum select
    if (resolvedSchema.enum) {
        const noValSelect = { label: '-- Select --', value: '' };
        return (
            <Controller
                key={fieldName}
                name={fieldName as any}
                control={control}
                render={({ field }) => (
                    <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                            {getTitle(resolvedSchema, property)}
                        </label>
                        <SelectInput
                            {...field}
                            className="spark-select"
                            options={[noValSelect, ...resolvedSchema.enum.map((val: string) => ({ label: val, value: val }))]}
                        />
                    </div>
                )}
            />
        );
    }

    // Boolean checkbox
    if (propertySchema.type === 'boolean') {
        return (
            <Controller
                key={fieldName}
                name={fieldName as any}
                control={control}
                render={({ field }) => (
                    <Checkbox
                        {...field}
                        checked={field.value || false}
                        onChange={(e) => field.onChange(e.target.checked)}
                        onBlur={field.onBlur}
                        label={getTitle(propertySchema, property)}
                    />
                )}
            />
        );
    }

    // Number input
    if (propertySchema.type === 'number' || propertySchema.type === 'integer') {
        return (
            <Controller
                key={fieldName}
                name={fieldName as any}
                control={control}
                render={({ field }) => (
                    <TextInput
                        {...field}
                        type="number"
                        className="spark-input"
                        label={getTitle(propertySchema, property)}
                    />
                )}
            />
        );
    }

    // Default text input
    return (
        <Controller
            key={fieldName}
            name={fieldName as any}
            control={control}
            render={({ field }) => (
                <TextInput
                    {...field}
                    type="text"
                    onBlur={field.onBlur}
                    label={getTitle(propertySchema, property)}
                />
            )}
        />
    );
};